/**
 * 3D model player (EAA-1642) — three.js viewer with animation controls & view-time gate.
 *
 * Renders a GLB/glTF model on a WebGL canvas with orbit controls. When the model
 * contains embedded AnimationClips, an audio-player-style transport bar appears
 * with play/pause, seek scrubber, and time display.
 *
 * EAA-1642 (this slice) adds two ways to drive those clips:
 *   1. A button per clip below the view — the model may ship several, and only
 *      the first was ever reachable before.
 *   2. Click-to-play object triggers — the author maps a glTF object name to a
 *      clip in the editor, and clicking that object in the scene plays it.
 * Both feed the SAME transport bar, so there is one notion of "the clip that is
 * currently playing" no matter how it was started.
 */

import * as React from 'react';
import {
    Scene,
    PerspectiveCamera,
    WebGLRenderer,
    AmbientLight,
    DirectionalLight,
    HemisphereLight,
    Box3,
    Vector3,
    Color,
    AnimationMixer,
    Clock,
    LoopRepeat,
    LoopOnce,
    SRGBColorSpace,
    NoToneMapping,
    PMREMGenerator,
    Raycaster,
    Vector2,
    Box3Helper,
    type Object3D,
    type Group,
    type AnimationClip,
    type AnimationAction,
} from 'three';
import { cn, Button } from '@ai-content-hub/content-kit';
import { useLocale, useContentPlayerActions } from '@ai-content-hub/content-core';
import type { Content3DData, Content3DConfig, Content3DAnswer, LightPreset } from './schema';
import { loadModel3D } from './lib/loadModel';
import { OBJECT_PATH_SEP, findObjectByPath } from './lib/modelOutline';
import { subscribePreviewHighlight } from './lib/previewHighlight';
import { setModel3DTenantId } from './api';

export interface Content3DPlayerProps {
    data: Content3DData;
    config?: Content3DConfig;
    answer?: Content3DAnswer | null;
    onAnswer?: (answer: Content3DAnswer) => void;
    showFeedback?: boolean;
    isReview?: boolean;
    disabled?: boolean;
    className?: string;
    /** Tenant ID for staff JWT context — used by model token API calls. */
    tenantId?: string;
}

/** Compute the fitting distance for the model in the viewport. */
function computeFitDistance(size: Vector3, camera: PerspectiveCamera): number {
    const halfFovY = (camera.fov * Math.PI) / 360;
    const aspect = camera.aspect || 1;
    const distForHeight = (size.y / 2) / Math.tan(halfFovY);
    const distForWidth = (size.x / 2) / (Math.tan(halfFovY) * aspect);
    const distForDepth = size.z / 2;
    return (Math.max(distForHeight, distForWidth) + distForDepth) * 1.15;
}

/**
 * Center the model at origin and fit the camera (instant snap).
 * Physically moves model.position so bbox center = (0,0,0).
 * Camera is placed straight ahead on the Z axis.
 */
function frameModel(model: Group, camera: PerspectiveCamera): void {
    // 1. Force matrix update so animated transforms are current
    model.updateMatrixWorld(true);

    // 2. Compute bounding box in world space
    const box = new Box3().setFromObject(model);
    const center = box.getCenter(new Vector3());

    // 3. Move model so bbox center sits at world origin
    model.position.x -= center.x;
    model.position.y -= center.y;
    model.position.z -= center.z;

    // 4. Recompute after move for accurate size
    model.updateMatrixWorld(true);
    const boxAfter = new Box3().setFromObject(model);
    const size = boxAfter.getSize(new Vector3());

    // 5. Position camera on Z axis looking at origin
    const distance = computeFitDistance(size, camera);
    camera.position.set(0, 0, distance);
    camera.near = Math.max(0.01, distance / 100);
    camera.far = distance * 100;
    camera.updateProjectionMatrix();
    camera.lookAt(0, 0, 0);
}


/** Cyan, matching the active-clip button and the auto-focus toggle. */
const HIGHLIGHT_COLOR = 0x22d3ee;

interface MaterialLike {
    clone: () => MaterialLike;
    dispose?: () => void;
    emissive?: Color;
    emissiveIntensity?: number;
    color?: Color;
    metalness?: number;
    roughness?: number;
    toneMapped?: boolean;
}

/**
 * Tint one material clone so the highlighted object is unmistakable.
 *
 * The first attempt only nudged `emissive`, which on a textured PBR material
 * in a bright studio rig is nearly invisible — the object had to CHANGE
 * COLOUR, not glow slightly. So the base colour is overwritten too, metalness
 * dropped and roughness raised so the tint is not eaten by reflections, and
 * tone mapping disabled so the emissive is not compressed back down.
 */
function tintMaterial(material: MaterialLike): void {
    if (material.color) material.color = new Color(HIGHLIGHT_COLOR);
    if (material.emissive) {
        material.emissive = new Color(HIGHLIGHT_COLOR);
        material.emissiveIntensity = 1.2;
    }
    if (typeof material.metalness === 'number') material.metalness = 0;
    if (typeof material.roughness === 'number') material.roughness = 0.85;
    material.toneMapped = false;
}

/** Pulse depth — emissive swings between (1 ± this) × the base intensity. */
const PULSE_AMOUNT = 0.55;
const PULSE_SPEED = 4.0;

/**
 * EAA-1642: highlight the object a learner just clicked, or the one the author
 * selected in the editor's object list.
 *
 * Materials are CLONED before being tinted. glTF exporters share one material
 * across many meshes, so tinting in place would light up unrelated parts of the
 * scene; the clone is disposed when the highlight is cleared.
 *
 * The object also pulses. A static tint is easy to lose in a busy scene, and a
 * slow shine reads as "this one" at a glance without needing a legend.
 */
function highlightObject(target: Object3D, scene: Scene): {
    clear: () => void;
    refresh: (elapsed: number) => void;
} {
    const restores: Array<() => void> = [];
    const pulsing: MaterialLike[] = [];

    target.traverse((child) => {
        const mesh = child as unknown as { isMesh?: boolean; material?: MaterialLike | MaterialLike[] };
        if (!mesh.isMesh || !mesh.material) return;
        const original = mesh.material;
        const clone = Array.isArray(original)
            ? original.map((m) => m.clone())
            : original.clone();
        const clones = Array.isArray(clone) ? clone : [clone];
        clones.forEach((m) => {
            tintMaterial(m);
            if (m.emissive) pulsing.push(m);
        });
        mesh.material = clone;
        restores.push(() => {
            mesh.material = original;
            clones.forEach((c) => c.dispose?.());
        });
    });

    const box = new Box3().setFromObject(target);
    const helper = new Box3Helper(box, new Color(HIGHLIGHT_COLOR));
    // Draw the box THROUGH the scene. Depth-tested like ordinary geometry, a
    // box around anything enclosed by or behind other objects — a backdrop
    // plane, a part inside a housing — is present but completely hidden, which
    // reads as "the highlight does not work".
    const helperMaterial = helper.material as {
        depthTest?: boolean;
        depthWrite?: boolean;
        transparent?: boolean;
    };
    helperMaterial.depthTest = false;
    helperMaterial.depthWrite = false;
    helperMaterial.transparent = true;
    helper.renderOrder = 999;
    scene.add(helper);

    return {
        refresh: (elapsed: number) => {
            // The box has to track the object while its clip animates it.
            box.setFromObject(target);
            const pulse = 1 + Math.sin(elapsed * PULSE_SPEED) * PULSE_AMOUNT;
            pulsing.forEach((m) => { m.emissiveIntensity = 1.2 * pulse; });
        },
        clear: () => {
            scene.remove(helper);
            helper.geometry.dispose();
            (helper.material as { dispose?: () => void })?.dispose?.();
            restores.forEach((restore) => restore());
        },
    };
}

/**
 * EAA-1642: pointer thresholds separating an object-trigger click from an orbit
 * drag on the very same canvas.
 */
const DRAG_SLOP_PX = 6;
const CLICK_MAX_MS = 700;
/** ~15Hz — a hover cursor change does not need per-frame raycasting. */
const HOVER_THROTTLE_MS = 66;

/** Format seconds as mm:ss */
function formatTime(sec: number): string {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Apply a lighting rig to the scene based on the selected preset.
 *
 * - **studio**: 3-point light rig (key + fill + back) + strong ambient + hemisphere.
 *   Designed for product/model showcase — bright, even, minimal shadows.
 * - **outdoor**: Warm key light (sun) + cool hemisphere (sky/ground) + soft ambient.
 * - **soft**: High ambient + hemisphere only, no harsh directional shadows.
 * - **neutral**: Balanced single directional + ambient (the original default).
 */
function setupLighting(scene: Scene, preset: LightPreset = 'studio'): void {
    switch (preset) {
        case 'studio': {
            // Hemisphere light for ambient sky/ground gradient
            const hemi = new HemisphereLight(0xffffff, 0xb0b0b0, 1.2);
            scene.add(hemi);
            // Ambient fill
            scene.add(new AmbientLight(0xffffff, 0.6));
            // Key light (front-right, warm)
            const key = new DirectionalLight(0xfff5e6, 1.5);
            key.position.set(4, 6, 5);
            scene.add(key);
            // Fill light (front-left, cooler)
            const fill = new DirectionalLight(0xe6f0ff, 0.8);
            fill.position.set(-4, 4, 3);
            scene.add(fill);
            // Back/rim light
            const rim = new DirectionalLight(0xffffff, 0.5);
            rim.position.set(0, 3, -5);
            scene.add(rim);
            break;
        }
        case 'outdoor': {
            // Sun
            const sun = new DirectionalLight(0xffeedd, 2.0);
            sun.position.set(5, 10, 5);
            scene.add(sun);
            // Sky/ground hemisphere
            const sky = new HemisphereLight(0x87ceeb, 0x556b2f, 1.0);
            scene.add(sky);
            // Subtle ambient
            scene.add(new AmbientLight(0xffffff, 0.3));
            break;
        }
        case 'soft': {
            // Strong ambient for even lighting
            scene.add(new AmbientLight(0xffffff, 1.4));
            // Gentle hemisphere for subtle gradient
            const hemi = new HemisphereLight(0xffffff, 0xd0d0d0, 0.8);
            scene.add(hemi);
            // Very soft directional for minimal shadow definition
            const soft = new DirectionalLight(0xffffff, 0.4);
            soft.position.set(2, 5, 3);
            scene.add(soft);
            break;
        }
        case 'neutral':
        default: {
            scene.add(new AmbientLight(0xffffff, 1.0));
            const dir = new DirectionalLight(0xffffff, 1.2);
            dir.position.set(3, 5, 4);
            scene.add(dir);
            // Secondary fill from the other side
            const fill = new DirectionalLight(0xffffff, 0.5);
            fill.position.set(-3, 3, -2);
            scene.add(fill);
            break;
        }
    }
}

// ── Animation transport bar (audio-player style) ────────────────────────────
interface AnimationControlsProps {
    playing: boolean;
    /** A one-shot clip that has ended: the play button means replay. */
    finished: boolean;
    replayLabel: string;
    currentTime: number;
    duration: number;
    onPlayPause: () => void;
    onSeek: (time: number) => void;
    clipName?: string;
}

const animBarStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 12px',
    background: 'rgba(0,0,0,0.55)',
    backdropFilter: 'blur(6px)',
    color: '#fff',
    fontSize: 12,
    zIndex: 10,
    userSelect: 'none',
};

const playBtnStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    color: '#fff',
    cursor: 'pointer',
    padding: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    flexShrink: 0,
};

function AnimationControls({ playing, finished, replayLabel, currentTime, duration, onPlayPause, onSeek, clipName }: AnimationControlsProps) {
    const pct = duration > 0 ? (currentTime / duration) * 100 : 0;
    return (
        <div className="content-3d-player-anim-bar" style={animBarStyle}>
            {/* Cross-browser range slider thumb styling */}
            <style>{`
                .content-3d-player-anim-bar input[type=range]::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    width: 12px; height: 12px;
                    border-radius: 50%;
                    background: #22d3ee;
                    border: none;
                    cursor: pointer;
                    margin-top: -4px;
                    box-shadow: 0 0 4px rgba(0,0,0,0.3);
                }
                .content-3d-player-anim-bar input[type=range]::-moz-range-thumb {
                    width: 12px; height: 12px;
                    border-radius: 50%;
                    background: #22d3ee;
                    border: none;
                    cursor: pointer;
                    box-shadow: 0 0 4px rgba(0,0,0,0.3);
                }
                .content-3d-player-anim-bar input[type=range]::-webkit-slider-runnable-track {
                    height: 4px;
                    border-radius: 2px;
                    background: linear-gradient(to right, #22d3ee ${pct}%, rgba(255,255,255,0.25) ${pct}%);
                }
                .content-3d-player-anim-bar input[type=range]::-moz-range-track {
                    height: 4px;
                    border-radius: 2px;
                    background: rgba(255,255,255,0.25);
                }
                .content-3d-player-anim-bar input[type=range]::-moz-range-progress {
                    height: 4px;
                    border-radius: 2px;
                    background: #22d3ee;
                }
            `}</style>
            <button
                type="button"
                onClick={onPlayPause}
                style={playBtnStyle}
                title={finished ? replayLabel : playing ? 'Pause' : 'Play'}
                aria-label={finished ? replayLabel : playing ? 'Pause animation' : 'Play animation'}
            >
                {finished ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 12a9 9 0 1 0 3-6.7" />
                        <polyline points="3 4 3 10 9 10" />
                    </svg>
                ) : playing ? (
                    /* Pause icon */
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <rect x="5" y="4" width="5" height="16" rx="1" />
                        <rect x="14" y="4" width="5" height="16" rx="1" />
                    </svg>
                ) : (
                    /* Play icon */
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6 4l14 8-14 8z" />
                    </svg>
                )}
            </button>
            <span style={{ fontVariantNumeric: 'tabular-nums', minWidth: 36, textAlign: 'center', flexShrink: 0 }}>
                {formatTime(currentTime)}
            </span>
            <input
                type="range"
                min={0}
                max={duration || 1}
                step={0.01}
                value={currentTime}
                onChange={(e) => onSeek(parseFloat(e.target.value))}
                style={{
                    flex: 1,
                    height: 4,
                    cursor: 'pointer',
                    accentColor: '#22d3ee',
                    background: `linear-gradient(to right, #22d3ee ${pct}%, rgba(255,255,255,0.25) ${pct}%)`,
                    borderRadius: 2,
                    WebkitAppearance: 'none',
                    appearance: 'none' as const,
                    outline: 'none',
                }}
                aria-label="Animation seek"
            />
            <span style={{ fontVariantNumeric: 'tabular-nums', minWidth: 36, textAlign: 'center', flexShrink: 0 }}>
                {formatTime(duration)}
            </span>
            {clipName ? (
                <span style={{ fontSize: 10, opacity: 0.7, marginLeft: 4, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }} title={clipName}>
                    {clipName}
                </span>
            ) : null}
        </div>
    );
}

// ── Player component ────────────────────────────────────────────────────────
interface AnimationClipButtonsProps {
    clips: string[];
    activeClip: string;
    playing: boolean;
    onSelect: (clip: string) => void;
    onStop: () => void;
    stopLabel: string;
}

const clipButtonBase: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    minHeight: 34,
    padding: '6px 12px',
    borderRadius: 8,
    fontSize: 13,
    lineHeight: 1.3,
    cursor: 'pointer',
    transition: 'background 0.15s, border-color 0.15s, color 0.15s',
    maxWidth: 220,
};

/**
 * One button per embedded clip, below the canvas.
 *
 * The active clip is highlighted and its button doubles as play/pause, so the
 * row alone is enough to drive a model — the transport bar underneath the
 * canvas stays available for scrubbing.
 */
function AnimationClipButtons({ clips, activeClip, playing, onSelect, onStop, stopLabel }: AnimationClipButtonsProps) {
    return (
        <div
            className="content-3d-player-clips"
            style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}
        >
            {clips.map((clip) => {
                const isActive = clip === activeClip;
                const isPlaying = isActive && playing;
                return (
                    <button
                        key={clip}
                        type="button"
                        onClick={() => onSelect(clip)}
                        title={clip}
                        aria-pressed={isActive}
                        className={cn(
                            'content-3d-player-clip',
                            isActive && 'content-3d-player-clip--active',
                        )}
                        style={{
                            ...clipButtonBase,
                            border: isActive ? '1.5px solid #22d3ee' : '1px solid var(--border, #e5e7eb)',
                            background: isActive ? 'rgba(34,211,238,0.14)' : 'var(--background, #fff)',
                            color: isActive ? '#0e7490' : 'var(--foreground, #374151)',
                            fontWeight: isActive ? 600 : 400,
                        }}
                    >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            {isPlaying ? (
                                <>
                                    <rect x="5" y="4" width="5" height="16" rx="1" />
                                    <rect x="14" y="4" width="5" height="16" rx="1" />
                                </>
                            ) : (
                                <path d="M6 4l14 8-14 8z" />
                            )}
                        </svg>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{clip}</span>
                    </button>
                );
            })}
            {activeClip ? (
                <button
                    type="button"
                    onClick={onStop}
                    className="content-3d-player-clip-stop"
                    style={{
                        ...clipButtonBase,
                        border: '1px solid var(--border, #e5e7eb)',
                        background: 'transparent',
                        color: 'var(--muted-foreground, #6b7280)',
                    }}
                >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <rect x="5" y="5" width="14" height="14" rx="2" />
                    </svg>
                    <span>{stopLabel}</span>
                </button>
            ) : null}
        </div>
    );
}

export const Content3DPlayer: React.FC<Content3DPlayerProps> = ({
    data,
    config,
    answer,
    onAnswer,
    showFeedback = false,
    isReview = false,
    disabled = false,
    className,
    tenantId,
}) => {
    const playerActions = useContentPlayerActions();
    const { t } = useLocale({ config: config?.i18n });
    const mountRef = React.useRef<HTMLDivElement>(null);

    // Set module-level tenant context so requestModel3DToken resolves it.
    React.useEffect(() => { setModel3DTenantId(tenantId); }, [tenantId]);

    const [status, setStatus] = React.useState<'loading' | 'ready' | 'error'>('loading');
    const [dwellSeconds, setDwellSeconds] = React.useState(0);

    // Animation state
    const [animPlaying, setAnimPlaying] = React.useState(false);
    const [animTime, setAnimTime] = React.useState(0);
    const [animDuration, setAnimDuration] = React.useState(0);
    // EAA-1642: every embedded clip is now reachable, not just the first.
    // Filled from the parsed model — the editor's cached `animationNames` is an
    // authoring convenience, never the player's source of truth, because only
    // the real AnimationClips can actually be played.
    const [clipNames, setClipNames] = React.useState<string[]>([]);
    const [activeClip, setActiveClip] = React.useState('');
    /** Mirrors clipFinishedRef for rendering — the control becomes a replay. */
    const [clipFinished, setClipFinished] = React.useState(false);

    // Refs for the animation mixer/actions so the seek handler can access them
    // Set by the render effect; lets the editor's object list drive the preview.
    const highlightByPathRef = React.useRef<((path: string | null) => void) | null>(null);
    // A request that arrived before the model finished loading, or before a
    // reload re-installed the handler. Without this the first click after page
    // load is silently dropped, which is indistinguishable from a broken bus.
    const pendingHighlightRef = React.useRef<string | null>(null);
    const mixerRef = React.useRef<AnimationMixer | null>(null);
    /** clip name → prepared action, one entry per embedded AnimationClip. */
    const actionsRef = React.useRef<Map<string, AnimationAction>>(new Map());
    const activeClipRef = React.useRef('');
    /** A one-shot clip that has reached its end: the next play is a replay. */
    const clipFinishedRef = React.useRef(false);
    const clockRef = React.useRef<Clock | null>(null);
    const animPlayingRef = React.useRef(false);
    // Refs for re-center / focus button
    const cameraRef = React.useRef<PerspectiveCamera | null>(null);
    const controlsRef = React.useRef<{ update: () => void; target: Vector3 } | null>(null);
    const modelRef = React.useRef<Group | null>(null);
    // Auto-focus toggle: when ON, orbit target is locked to model center
    const [autoFocus, setAutoFocus] = React.useState(true);
    const autoFocusRef = React.useRef(true);

    const assetId = (data?.assetId ?? '').trim();
    const minNeeded = Math.max(0, data?.minViewSeconds ?? 0);
    // EAA-1642: both default OFF. The learner now starts the motion — via a
    // clip button or by clicking an object the author wired up — and a model
    // that span and played itself on load fought both. See defaultContent3DData.
    const autoRotate = data?.autoRotate ?? false;
    const autoPlayAnimation = data?.autoPlayAnimation ?? false;
    const showClipButtons = data?.showAnimationButtons ?? true;
    // EAA-1642: opt-in hiding of the transport bar. Defaults to showing it.
    // EAA-1642: the timeline is opt-in — hidden unless the author shows it.
    const showTimeline = data?.showAnimationTimeline ?? false;
    // EAA-1642: a clip plays ONCE by default and holds its final pose; the
    // play control then reads as a replay. Opt into looping with loopAnimation.
    const playOnce = !(data?.loopAnimation ?? false);
    // playClip is a no-dep useCallback, and the render effect installs the
    // actions once — both read the option through a ref so toggling it never
    // rebuilds the renderer.
    const playOnceRef = React.useRef(playOnce);
    playOnceRef.current = playOnce;

    // EAA-1642: object → clip triggers, keyed by the raw glTF node name.
    const interactions = data?.objectInteractions;
    const interactionMap = React.useMemo(() => {
        const map = new Map<string, string>();
        (interactions ?? []).forEach(({ objectName, clipName }) => {
            const obj = (objectName ?? '').trim();
            const clip = (clipName ?? '').trim();
            if (obj && clip) map.set(obj, clip);
        });
        return map;
    }, [interactions]);

    // The pointer listeners are installed once, inside the model-loading effect.
    // Reading the map through a ref keeps re-authored interactions live without
    // putting `objectInteractions` in that effect's deps — which would tear the
    // renderer down and re-download the model on every edit.
    const interactionMapRef = React.useRef(interactionMap);
    interactionMapRef.current = interactionMap;

    // ── Focus / re-center handler (toggle) ──────────────────────────────────
    const handleFocusToggle = React.useCallback(() => {
        const next = !autoFocusRef.current;
        autoFocusRef.current = next;
        setAutoFocus(next);
        // When turning ON, immediately re-center
        if (next) {
            const model = modelRef.current;
            const camera = cameraRef.current;
            const ctrl = controlsRef.current;
            if (model && camera) {
                frameModel(model, camera);
                if (ctrl) {
                    ctrl.target.set(0, 0, 0);
                }
            }
        }
    }, []);

    // ── three.js scene lifecycle ────────────────────────────────────────────
    React.useEffect(() => {
        const mount = mountRef.current;
        if (!mount || !assetId) {
            if (!assetId) setStatus('error');
            return undefined;
        }

        let cancelled = false;
        let frameId = 0;
        let renderer: WebGLRenderer | null = null;
        let controls: { update: () => void; dispose: () => void; autoRotate: boolean; target: Vector3 } | null = null;
        let disposeModel: (() => void) | null = null;
        let resizeObserver: ResizeObserver | null = null;
        let disposePointerTriggers: (() => void) | null = null;
        // Captured for the cleanup below: the ref may point elsewhere by then.
        const actions = actionsRef.current;

        // Disable auto-focus when user interacts with the canvas
        const disableAutoFocus = () => {
            if (autoFocusRef.current) {
                autoFocusRef.current = false;
                setAutoFocus(false);
            }
        };

        const scene = new Scene();
        scene.background = new Color(data?.background || '#f3f4f6');
        const camera = new PerspectiveCamera(45, 1, 0.1, 1000);
        cameraRef.current = camera;
        setupLighting(scene, (data?.lightPreset as LightPreset) || 'studio');

        // Animation setup
        let mixer: AnimationMixer | null = null;
        const clock = new Clock();
        clockRef.current = clock;

        const setup = async () => {
            setStatus('loading');
            renderer = new WebGLRenderer({ antialias: true, alpha: true });
            renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
            // Correct color space for PBR textures
            renderer.outputColorSpace = SRGBColorSpace;
            renderer.toneMapping = NoToneMapping;

            // Environment map: use uploaded HDR if available, else RoomEnvironment.
            const pmremGenerator = new PMREMGenerator(renderer);
            pmremGenerator.compileEquirectangularShader();
            const envAssetId = (data?.environmentAssetId ?? '').trim();
            if (envAssetId) {
                // Load custom HDR environment via one-time token
                try {
                    const { requestModel3DToken } = await import('./api');
                    const { RGBELoader } = await import('three/examples/jsm/loaders/RGBELoader.js');
                    if (cancelled) { pmremGenerator.dispose(); return; }
                    const { url } = await requestModel3DToken(envAssetId);
                    const hdrTexture = await new Promise<import('three').DataTexture>((resolve, reject) => {
                        new RGBELoader().load(url, resolve, undefined, reject);
                    });
                    if (cancelled) { hdrTexture.dispose(); pmremGenerator.dispose(); return; }
                    const envMap = pmremGenerator.fromEquirectangular(hdrTexture).texture;
                    scene.environment = envMap;
                    scene.background = envMap; // show HDR as visible background
                    hdrTexture.dispose();
                } catch (err) {
                    // Fallback to RoomEnvironment on HDR load failure
                    console.warn('[content-3d] Failed to load HDR environment, using fallback:', err);
                    const { RoomEnvironment } = await import('three/examples/jsm/environments/RoomEnvironment.js');
                    if (!cancelled) {
                        scene.environment = pmremGenerator.fromScene(new RoomEnvironment()).texture;
                    }
                }
            } else {
                // Default: neutral studio environment (solid color background)
                const { RoomEnvironment } = await import('three/examples/jsm/environments/RoomEnvironment.js');
                if (cancelled) { pmremGenerator.dispose(); return; }
                scene.environment = pmremGenerator.fromScene(new RoomEnvironment()).texture;
            }
            pmremGenerator.dispose();
            const resize = () => {
                if (!renderer) return;
                const w = mount.clientWidth || 1;
                const h = mount.clientHeight || 1;
                renderer.setSize(w, h, false);
                camera.aspect = w / h;
                camera.updateProjectionMatrix();
            };
            resize();
            mount.appendChild(renderer.domElement);

            const { OrbitControls } = await import('three/examples/jsm/controls/OrbitControls.js');
            if (cancelled) return;
            controls = new OrbitControls(camera, renderer.domElement) as unknown as typeof controls;
            (controls as unknown as { enableDamping: boolean }).enableDamping = true;
            (controls as unknown as { dampingFactor: number }).dampingFactor = 0.08;
            controls!.autoRotate = autoRotate;
            (controls as unknown as { autoRotateSpeed: number }).autoRotateSpeed = 1.2;
            controlsRef.current = controls as unknown as typeof controlsRef.current;

            // Register interaction listeners to cancel auto-focus
            // EAA-1642: auto-focus is released by an actual orbit gesture, not
            // by the press that starts one. It used to hang off `mousedown` /
            // `touchstart`, which meant a tap on an object — now a legitimate
            // way to trigger an animation — also knocked the camera out of
            // auto-focus. The drag detection in handlePointerMove below does it.
            renderer.domElement.addEventListener('wheel', disableAutoFocus);

            let model: Group | null = null;
            let clips: AnimationClip[] = [];
            try {
                const loaded = await loadModel3D(assetId);
                if (cancelled) {
                    loaded.dispose();
                    return;
                }
                model = loaded.scene;
                modelRef.current = model;
                clips = loaded.animations;
                disposeModel = loaded.dispose;
                scene.add(model);
                frameModel(model, camera);
                // Model is now centered at origin — set controls to orbit around (0,0,0)
                if (controls) {
                    controls.target.set(0, 0, 0);
                    // Do NOT call controls.update() here — it would override
                    // the camera position that frameModel just set
                }
                setStatus('ready');
            } catch {
                if (!cancelled) setStatus('error');
                return;
            }

            // EAA-1642: prepare an action for EVERY embedded clip, not just
            // clips[0]. A clip button and an object trigger both need to reach
            // any of them; only the active one is ever un-paused.
            // A re-load (a swapped asset) must not leave the previous model's
            // clip row and active-clip state on screen.
            setClipNames([]);
            setActiveClip('');
            activeClipRef.current = '';
            setAnimTime(0);
            setAnimDuration(0);
            setAnimPlaying(false);
            animPlayingRef.current = false;
            if (clips.length > 0 && model) {
                mixer = new AnimationMixer(model);
                mixerRef.current = mixer;

                actions.clear();
                const names: string[] = [];
                clips.forEach((clip, i) => {
                    const name = clip.name || `Animation ${i + 1}`;
                    if (actions.has(name)) return; // duplicate clip name — first wins
                    const action = mixer!.clipAction(clip);
                    action.setLoop(
                        playOnceRef.current ? LoopOnce : LoopRepeat,
                        playOnceRef.current ? 1 : Infinity,
                    );
                    action.clampWhenFinished = playOnceRef.current;
                    actions.set(name, action);
                    names.push(name);
                });
                setClipNames(names);

                const first = names[0];
                const firstAction = actions.get(first);
                if (first && firstAction) {
                    activeClipRef.current = first;
                    setActiveClip(first);
                    setAnimDuration(firstAction.getClip().duration);
                    // Always play() so the mixer owns the action; `paused`
                    // decides whether it advances. Pausing at frame 0 keeps the
                    // rest pose visible instead of a blank first frame.
                    firstAction.play();
                    firstAction.paused = !autoPlayAnimation;
                    animPlayingRef.current = autoPlayAnimation;
                    setAnimPlaying(autoPlayAnimation);
                }
            }

            // EAA-1642: a one-shot clip ends on its own, so the transport has
            // to hear about it — otherwise the bar keeps claiming it is playing
            // and the play button does nothing when pressed.
            const handleFinished = () => {
                clipFinishedRef.current = true;
                animPlayingRef.current = false;
                setAnimPlaying(false);
                setClipFinished(true);
            };
            mixer?.addEventListener('finished', handleFinished);

            // ── EAA-1642: click-an-object → play-a-clip ──────────────────────
            // A click is only a trigger when the pointer barely moved: the same
            // canvas is an orbit surface, and a drag that ends over an object
            // must not fire its animation.
            const raycaster = new Raycaster();
            const pointer = new Vector2();
            let highlight: ReturnType<typeof highlightObject> | null = null;

            const clearHighlight = () => {
                highlight?.clear();
                highlight = null;
            };
            const setHighlight = (target: Object3D) => {
                clearHighlight();
                highlight = highlightObject(target, scene);
            };
            // EAA-1642: the editor's object list highlights through this — see
            // lib/previewHighlight.ts for why it is a bus and not shared state.
            highlightByPathRef.current = (path) => {
                if (!path || !model) {
                    clearHighlight();
                    return;
                }
                const target = findObjectByPath(model, path);
                if (target) {
                    setHighlight(target);
                    return;
                }
                console.warn('[content-3d] No object in the model matches the path:', path);
                clearHighlight();
            };

            // Replay whatever was selected while the model was still loading.
            if (pendingHighlightRef.current !== null) {
                highlightByPathRef.current(pendingHighlightRef.current);
            }
            let pressX = 0;
            let pressY = 0;
            let pressAt = 0;
            let pressing = false;
            let lastHoverAt = 0;

            /** Resolve a pointer event to the clip an author mapped, if any. */
            const clipAtPointer = (event: PointerEvent): string | null => {
                const map = interactionMapRef.current;
                if (map.size === 0 || !model || !renderer) return null;
                const rect = renderer.domElement.getBoundingClientRect();
                if (!rect.width || !rect.height) return null;
                pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
                pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
                raycaster.setFromCamera(pointer, camera);
                const hits = raycaster.intersectObject(model, true);
                for (const hit of hits) {
                    // Rebuild the hit object's path the same way the editor
                    // flattened the tree — named ancestors only, unnamed nodes
                    // transparent — then match longest prefix first so a
                    // mapping on one part beats a mapping on its whole group.
                    const segments: string[] = [];
                    let node: Object3D | null = hit.object;
                    while (node && node !== model) {
                        const name = (node.name ?? '').trim();
                        if (name) segments.unshift(name);
                        node = node.parent;
                    }
                    for (let depth = segments.length; depth > 0; depth -= 1) {
                        const byPath = map.get(segments.slice(0, depth).join(OBJECT_PATH_SEP));
                        if (byPath) return byPath;
                        // Mappings authored before paths existed are bare names.
                        const byName = map.get(segments[depth - 1]);
                        if (byName) return byName;
                    }
                }
                return null;
            };

            const handlePointerDown = (event: PointerEvent) => {
                pressX = event.clientX;
                pressY = event.clientY;
                pressAt = performance.now();
                pressing = true;
            };

            const handlePointerUp = (event: PointerEvent) => {
                pressing = false;
                const moved = Math.hypot(event.clientX - pressX, event.clientY - pressY);
                if (moved > DRAG_SLOP_PX || performance.now() - pressAt > CLICK_MAX_MS) return; // an orbit drag
                // EAA-1642: play only. The highlight is an AUTHORING aid, driven
                // solely by clicking a name in the editor's object list — a
                // learner clicking the model should see the animation, not the
                // scene light up cyan.
                const clip = clipAtPointer(event);
                if (clip) playClipRef.current(clip);
            };

            const handlePointerMove = (event: PointerEvent) => {
                if (!renderer) return;
                if (pressing && Math.hypot(event.clientX - pressX, event.clientY - pressY) > DRAG_SLOP_PX) {
                    disableAutoFocus(); // this press has become an orbit drag
                }
                // Raycasting a few-hundred-mesh scene on every mousemove is
                // wasteful; ~15Hz is well past what a cursor change needs.
                const now = performance.now();
                if (now - lastHoverAt < HOVER_THROTTLE_MS) return;
                lastHoverAt = now;
                renderer.domElement.style.cursor = clipAtPointer(event) ? 'pointer' : '';
            };

            const handlePointerCancel = () => { pressing = false; };

            renderer.domElement.addEventListener('pointerdown', handlePointerDown);
            renderer.domElement.addEventListener('pointerup', handlePointerUp);
            renderer.domElement.addEventListener('pointermove', handlePointerMove);
            renderer.domElement.addEventListener('pointercancel', handlePointerCancel);
            renderer.domElement.addEventListener('pointerleave', handlePointerCancel);
            disposePointerTriggers = () => {
                mixer?.removeEventListener('finished', handleFinished);
                clearHighlight();
                highlightByPathRef.current = null;
                const canvas = renderer?.domElement;
                if (!canvas) return;
                canvas.removeEventListener('pointerdown', handlePointerDown);
                canvas.removeEventListener('pointerup', handlePointerUp);
                canvas.removeEventListener('pointermove', handlePointerMove);
                canvas.removeEventListener('pointercancel', handlePointerCancel);
                canvas.removeEventListener('pointerleave', handlePointerCancel);
            };

            resizeObserver = new ResizeObserver(resize);
            resizeObserver.observe(mount);

            const focusTargetPos = new Vector3(); // desired camera position
            const LERP_SPEED = 3.0; // higher = faster smooth transition

            const animate = () => {
                frameId = requestAnimationFrame(animate);
                const delta = clock.getDelta();
                if (mixer && animPlayingRef.current) {
                    mixer.update(delta);
                    // Update time display
                    const action = actionsRef.current.get(activeClipRef.current);
                    if (action) {
                        setAnimTime(action.time % (action.getClip().duration || 1));
                    }
                }
                // Auto-focus: model was centered at origin on load.
                // Just adjust camera distance to fit current animation bounds.
                if (autoFocusRef.current && controls && model) {
                    model.updateMatrixWorld(true);
                    const box = new Box3().setFromObject(model);
                    const size = box.getSize(new Vector3());
                    const idealDist = computeFitDistance(size, camera);

                    // Update near/far
                    camera.near = Math.max(0.01, idealDist / 100);
                    camera.far = idealDist * 100;
                    camera.updateProjectionMatrix();

                    // Smoothly adjust camera Z distance, keep looking at origin
                    const t = Math.min(1, delta * LERP_SPEED);
                    focusTargetPos.set(0, 0, idealDist);
                    camera.position.lerp(focusTargetPos, t);
                    controls.target.set(0, 0, 0);
                    camera.lookAt(0, 0, 0);
                } else {
                    // Free orbit mode — OrbitControls drives the camera
                    controls?.update();
                }
                highlight?.refresh(clock.getElapsedTime());
                renderer?.render(scene, camera);
            };
            animate();
        };

        setup();

        return () => {
            cancelled = true;
            if (frameId) cancelAnimationFrame(frameId);
            resizeObserver?.disconnect();
            controls?.dispose();
            controlsRef.current = null;
            // Remove interaction listeners
            if (renderer) {
                renderer.domElement.removeEventListener('wheel', disableAutoFocus);
            }
            disposePointerTriggers?.();
            if (mixer) {
                mixer.stopAllAction();
                mixerRef.current = null;
                actions.clear();
                activeClipRef.current = '';
            }
            disposeModel?.();
            // Dispose environment texture
            if (scene.environment) {
                scene.environment.dispose();
                scene.environment = null;
            }
            if (renderer) {
                renderer.dispose();
                renderer.domElement.remove();
            }
        };
    }, [assetId, autoRotate, autoPlayAnimation, data?.background, data?.lightPreset, data?.environmentAssetId]);

    // ── Animation controls handlers ─────────────────────────────────────────
    const handlePlayPause = React.useCallback(() => {
        const action = actionsRef.current.get(activeClipRef.current);
        if (!action) return;
        // A finished one-shot cannot be un-paused — it has no time left to
        // run. Restarting is what the control means at that point.
        if (clipFinishedRef.current) {
            playClipRef.current(activeClipRef.current);
            return;
        }
        const nextPlaying = !animPlayingRef.current;
        action.paused = !nextPlaying;
        animPlayingRef.current = nextPlaying;
        setAnimPlaying(nextPlaying);
    }, []);

    /**
     * EAA-1642: make `name` the active clip and start it from the top.
     *
     * Clips are exclusive — switching stops the previous action instead of
     * cross-fading, because these are unrelated scene animations (a lid opens,
     * a turbine spins), not locomotion states that blend into one another.
     */
    const playClip = React.useCallback((name: string) => {
        const actions = actionsRef.current;
        const next = actions.get(name);
        if (!next) return;

        const previous = actions.get(activeClipRef.current);
        if (previous && previous !== next) previous.stop();

        activeClipRef.current = name;
        setActiveClip(name);
        setAnimDuration(next.getClip().duration);

        next.reset();
        // EAA-1642: honour the play-once option. clampWhenFinished holds the
        // last frame instead of snapping back to the rest pose, which reads as
        // "finished" rather than "never played".
        next.setLoop(playOnceRef.current ? LoopOnce : LoopRepeat, playOnceRef.current ? 1 : Infinity);
        next.clampWhenFinished = playOnceRef.current;
        clipFinishedRef.current = false;
        setClipFinished(false);
        next.paused = false;
        next.play();
        animPlayingRef.current = true;
        setAnimPlaying(true);
        setAnimTime(0);
    }, []);

    // A clip button doubles as play/pause once its clip is the active one.
    const handleClipSelect = React.useCallback((name: string) => {
        if (name === activeClipRef.current) {
            handlePlayPause();
            return;
        }
        playClip(name);
    }, [handlePlayPause, playClip]);

    const handleStop = React.useCallback(() => {
        const action = actionsRef.current.get(activeClipRef.current);
        const mixer = mixerRef.current;
        if (!action || !mixer) return;
        clipFinishedRef.current = false;
        setClipFinished(false);
        action.reset();
        action.paused = true;
        action.time = 0;
        mixer.update(0); // snap the pose back to the rest frame
        animPlayingRef.current = false;
        setAnimPlaying(false);
        setAnimTime(0);
    }, []);

    // Object-click triggers fire from inside the render effect; reach the
    // handler through a ref so the effect never depends on it.
    const playClipRef = React.useRef(playClip);
    playClipRef.current = playClip;

    // EAA-1642: highlight requests coming from the editor's object list.
    // Scoped by assetId so a page with two models highlights in the right one.
    React.useEffect(() => {
        return subscribePreviewHighlight((request) => {
            // Only ignore when BOTH sides name an asset and they disagree — an
            // unsaved edit can leave the preview's copy of the content without
            // an assetId yet, and filtering that out would drop every request
            // for exactly the content being authored.
            if (request.assetId && assetId && request.assetId !== assetId) return;
            // Buffered when the model has not parsed yet; setup replays it.
            pendingHighlightRef.current = request.objectPath;
            highlightByPathRef.current?.(request.objectPath);
        });
    }, [assetId]);

    const handleSeek = React.useCallback((time: number) => {
        const action = actionsRef.current.get(activeClipRef.current);
        const mixer = mixerRef.current;
        if (!action || !mixer) return;
        action.time = time;
        mixer.update(0); // force the pose to update
        setAnimTime(time);
    }, []);

    // ── View-time completion gate (mirrors documents player) ────────────────
    React.useEffect(() => {
        if (isReview || showFeedback || disabled || minNeeded <= 0) return undefined;
        const timer = setInterval(() => {
            if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
            setDwellSeconds((s) => s + 1);
        }, 1000);
        return () => clearInterval(timer);
    }, [isReview, showFeedback, disabled, minNeeded]);

    const gateOk = minNeeded <= 0 || dwellSeconds >= minNeeded;
    const showMarkFooter = !showFeedback && !isReview && !disabled;

    const handleMarkDone = () => {
        if (!gateOk) return;
        onAnswer?.({ completed: true, viewedSeconds: dwellSeconds });
        playerActions?.requestSubmit();
    };

    return (
        <div
            className={cn('content-3d-player', className)}
            style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, gap: 16 }}
        >
            {(data?.description ?? '').trim() ? (
                <p className="content-3d-player-description text-muted-foreground text-sm px-1">
                    {data.description}
                </p>
            ) : null}

            <div
                className="content-3d-player-stage"
                style={{ position: 'relative', flex: '1 1 0%', minHeight: 420, overflow: 'hidden', borderRadius: 8, border: '1px solid var(--border, #e5e7eb)', background: 'var(--muted, #f3f4f6)' }}
            >
                <div ref={mountRef} className="content-3d-player-canvas" style={{ position: 'absolute', inset: 0 }} />
                {status === 'loading' ? (
                    <div className="content-3d-player-overlay" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: 'var(--muted-foreground, #6b7280)' }}>
                        {t('loading', 'Loading 3D model…')}
                    </div>
                ) : null}
                {status === 'error' ? (
                    <div className="content-3d-player-overlay content-3d-player-overlay--error" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: 'var(--destructive, #ef4444)' }}>
                        {t('loadError', 'Could not load the 3D model.')}
                    </div>
                ) : null}

                {/* Floating toolbar — always visible when model is ready */}
                {status === 'ready' ? (
                    <div
                        className="content-3d-player-toolbar"
                        style={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            display: 'flex',
                            gap: 4,
                            zIndex: 5,
                        }}
                    >
                        <button
                            type="button"
                            onClick={handleFocusToggle}
                            title={autoFocus ? 'Auto-focus ON (click to disable)' : 'Auto-focus OFF (click to enable)'}
                            aria-label={autoFocus ? 'Disable auto-focus' : 'Enable auto-focus'}
                            aria-pressed={autoFocus}
                            style={{
                                width: 32,
                                height: 32,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 6,
                                border: autoFocus ? '1.5px solid #22d3ee' : '1px solid rgba(0,0,0,0.12)',
                                background: autoFocus ? 'rgba(34,211,238,0.15)' : 'rgba(255,255,255,0.85)',
                                backdropFilter: 'blur(4px)',
                                cursor: 'pointer',
                                color: autoFocus ? '#0891b2' : '#374151',
                                transition: 'all 0.15s',
                            }}
                            onMouseEnter={(e) => {
                                (e.currentTarget as HTMLElement).style.background = autoFocus ? 'rgba(34,211,238,0.25)' : 'rgba(255,255,255,1)';
                            }}
                            onMouseLeave={(e) => {
                                (e.currentTarget as HTMLElement).style.background = autoFocus ? 'rgba(34,211,238,0.15)' : 'rgba(255,255,255,0.85)';
                            }}
                        >
                            {/* Crosshair / focus icon */}
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="3" />
                                <line x1="12" y1="2" x2="12" y2="6" />
                                <line x1="12" y1="18" x2="12" y2="22" />
                                <line x1="2" y1="12" x2="6" y2="12" />
                                <line x1="18" y1="12" x2="22" y2="12" />
                            </svg>
                        </button>
                    </div>
                ) : null}

                {/* Animation transport bar — shown only when the model has animations */}
                {showTimeline && clipNames.length > 0 && status === 'ready' ? (
                    <AnimationControls
                        playing={animPlaying}
                        finished={clipFinished}
                        replayLabel={t('replayAnimation', 'Replay')}
                        currentTime={animTime}
                        duration={animDuration}
                        onPlayPause={handlePlayPause}
                        onSeek={handleSeek}
                        clipName={activeClip}
                    />
                ) : null}
            </div>

            {showClipButtons && clipNames.length > 0 && status === 'ready' ? (
                <AnimationClipButtons
                    clips={clipNames}
                    activeClip={activeClip}
                    playing={animPlaying}
                    onSelect={handleClipSelect}
                    onStop={handleStop}
                    stopLabel={t('stopAnimation', 'Stop')}
                />
            ) : null}

            {(showFeedback || isReview) && answer?.completed ? (
                <div className="content-3d-player-feedback text-success text-sm font-medium px-1">
                    {t('completed', 'Completed')}
                </div>
            ) : null}

            {showMarkFooter ? (
                <div className="content-3d-player-actions flex-shrink-0 border-t border-border pt-3 flex flex-col gap-2">
                    {minNeeded > 0 && !gateOk ? (
                        <p className="content-3d-player-gate text-sm text-muted-foreground">
                            {t('dwellHint', 'Explore the model for at least {{seconds}}s to continue.')
                                .replace('{{seconds}}', String(minNeeded))}{' '}
                            ({dwellSeconds}s / {minNeeded}s)
                        </p>
                    ) : null}
                    <Button
                        type="button"
                        className="content-3d-player-mark-done w-full cursor-pointer"
                        disabled={!gateOk || status !== 'ready'}
                        onClick={handleMarkDone}
                    >
                        {t('markDone', 'Mark done')}
                    </Button>
                </div>
            ) : null}
        </div>
    );
};
